import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { INotificationCenterListResponse } from 'types/api/notificationCenterApi.interface'
import { INotificationCenterSlice, INotificationItem } from 'types/notificationCenter.interface'
import { baseUrl } from 'utils/axios'
import { notificationTypeCode, notificationTypeDef } from 'pages/NotificationCenter/notificationRegistry'
import {
  acknowledgeDemoNotification,
  demoNotificationPayload,
  markAllDemoNotificationsRead,
  markDemoNotificationRead,
  USE_NOTIFICATION_MOCKS,
} from 'pages/NotificationCenter/notificationDemoStore'

const initialState: INotificationCenterSlice = {
  isLoading: false,
  hasError: false,
  items: [],
  unreadCount: 0,
}

const mapResponse = (resp: INotificationCenterListResponse): { items: INotificationItem[]; unreadCount: number } => ({
  unreadCount: resp.unreadCount,
  items: resp.items.map((n) => {
    const type = notificationTypeCode(n.type)
    const def = notificationTypeDef(type)
    return { ...n, type, category: def.category, link: def.link }
  }),
})

export const getNotificationCenter = createAsyncThunk(
  'notificationCenter/get',
  async (params: any, thunkAPI) => {
    if (USE_NOTIFICATION_MOCKS) return demoNotificationPayload(thunkAPI.getState(), params?.roleId ?? null)
    try {
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('v1/notifications', { params })
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const markNotificationCenterRead = createAsyncThunk(
  'notificationCenter/markRead',
  async (id: string, thunkAPI) => {
    if (USE_NOTIFICATION_MOCKS) {
      markDemoNotificationRead(id)
      return demoNotificationPayload(thunkAPI.getState())
    }
    try {
      await baseUrl.patch(`v1/notifications/${id}/read`)
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('v1/notifications')
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const markAllNotificationCenterRead = createAsyncThunk(
  'notificationCenter/markAllRead',
  async (_: void, thunkAPI) => {
    if (USE_NOTIFICATION_MOCKS) {
      markAllDemoNotificationsRead()
      return demoNotificationPayload(thunkAPI.getState())
    }
    try {
      await baseUrl.post('v1/notifications/read-all')
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('v1/notifications')
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const acknowledgeNotificationCenter = createAsyncThunk(
  'notificationCenter/acknowledge',
  async (id: string, thunkAPI) => {
    if (USE_NOTIFICATION_MOCKS) {
      acknowledgeDemoNotification(id)
      return demoNotificationPayload(thunkAPI.getState())
    }
    try {
      await baseUrl.patch(`v1/notifications/${id}/acknowledge`)
      const resp: AxiosResponse<INotificationCenterListResponse> = await baseUrl.get('v1/notifications')
      return mapResponse(resp.data)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const applyPayload = (
  state: INotificationCenterSlice,
  { payload }: { payload: { items: INotificationItem[]; unreadCount: number } },
) => {
  state.isLoading = false
  state.items = payload.items
  state.unreadCount = payload.unreadCount
}

const notificationCenterSlice = createSlice({
  name: 'notificationCenter',
  initialState,
  reducers: {},
  extraReducers: {
    [getNotificationCenter.pending.type]: (state) => {
      state.isLoading = true
      state.hasError = false
    },
    [getNotificationCenter.fulfilled.type]: applyPayload,
    [getNotificationCenter.rejected.type]: (state) => {
      state.isLoading = false
      state.hasError = true
    },
    [markNotificationCenterRead.fulfilled.type]: applyPayload,
    [markAllNotificationCenterRead.fulfilled.type]: applyPayload,
    [acknowledgeNotificationCenter.fulfilled.type]: applyPayload,
  },
})

export default notificationCenterSlice.reducer
